// PushoverPanel.jsx — Admin Pushover notification configuration
import { useState, useEffect } from "react";
import { useTheme } from "../constants";
import { pushover as pushoverApi } from "../api";

function Toggle({ checked, onChange, label, desc, C }) {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 0", borderBottom: `1px solid ${C.border}` }}>
      <div style={{ flex: 1, minWidth: 0, marginRight: 12 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: C.text }}>{label}</div>
        {desc && <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>{desc}</div>}
      </div>
      <button onClick={() => onChange(!checked)}
        style={{ width: 42, height: 24, borderRadius: 12, border: "none", cursor: "pointer", background: checked ? "#22C55E" : C.border, position: "relative", flexShrink: 0, transition: "background 0.2s" }}>
        <div style={{ position: "absolute", top: 3, left: checked ? 21 : 3, width: 18, height: 18, borderRadius: "50%", background: "#fff", transition: "left 0.2s" }}/>
      </button>
    </div>
  );
}

export default function PushoverPanel({ onClose }) {
  const C = useTheme();
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    pushoverApi.get().then(c => setCfg(c)).catch(() => {});
  }, []);

  function update(key, val) {
    setCfg(p => ({ ...p, [key]: val }));
    setDirty(true);
    setTestResult(null);
  }

  async function save() {
    if (!cfg) return;
    setSaving(true);
    try {
      const updated = await pushoverApi.save({
        app_token: cfg.app_token || "",
        user_key: cfg.user_key || "",
        enabled: !!cfg.enabled,
        on_login: !!cfg.on_login,
        on_register: !!cfg.on_register,
        on_guest: !!cfg.on_guest,
        on_temp_room: !!cfg.on_temp_room,
      });
      setCfg(updated);
      setDirty(false);
    } catch (e) { alert(e.message); }
    setSaving(false);
  }

  async function test() {
    setTesting(true);
    setTestResult(null);
    try {
      await pushoverApi.test();
      setTestResult("success");
    } catch (e) {
      setTestResult("error");
    }
    setTesting(false);
    setTimeout(() => setTestResult(null), 4000);
  }

  return (
    <div style={{ position: "fixed", inset: 0, background: "#00000088", zIndex: 100, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 16, width: "100%", maxWidth: 460, maxHeight: "90vh", display: "flex", flexDirection: "column", overflow: "hidden" }}>

        {/* Header */}
        <div style={{ padding: "18px 20px 14px", borderBottom: `1px solid ${C.border}`, display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
          <div>
            <div style={{ fontSize: 17, fontWeight: 800, color: C.text, fontFamily: C.font }}>🔔 Pushover Notifications</div>
            <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>Get notified about logins and activity on your Tavern</div>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: C.muted, fontSize: 20, padding: 4, lineHeight: 1 }}>✕</button>
        </div>

        <div style={{ flex: 1, overflow: "auto", padding: "18px 20px" }}>
          {!cfg ? (
            <div style={{ color: C.muted, textAlign: "center", padding: 24 }}>Loading…</div>
          ) : (
            <>
              {/* Setup info */}
              <div style={{ background: `${C.accent}12`, border: `1px solid ${C.accent}33`, borderRadius: 10, padding: "10px 14px", marginBottom: 18, fontSize: 12, color: C.muted, lineHeight: 1.6 }}>
                Get your keys at <a href="https://pushover.net" target="_blank" rel="noreferrer" style={{ color: C.accent }}>pushover.net</a> — create an Application for the App Token, then use your User Key from your dashboard.
              </div>

              {/* Enable toggle */}
              <Toggle checked={!!cfg.enabled} onChange={v => update("enabled", v)}
                label="Enable Pushover" desc="Master switch for all notifications" C={C}/>

              {/* Credentials */}
              <div style={{ marginTop: 16, marginBottom: 16 }}>
                <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 1, marginBottom: 6 }}>App Token</label>
                <input value={cfg.app_token || ""} onChange={e => update("app_token", e.target.value)}
                  placeholder="a…"
                  style={{ width: "100%", background: C.inputBg, border: `1px solid ${C.border}`, borderRadius: 8, color: C.text, fontSize: 13, padding: "9px 12px", outline: "none", boxSizing: "border-box", marginBottom: 10, fontFamily: "monospace" }}/>
                <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 1, marginBottom: 6 }}>User Key</label>
                <input value={cfg.user_key || ""} onChange={e => update("user_key", e.target.value)}
                  placeholder="u…"
                  style={{ width: "100%", background: C.inputBg, border: `1px solid ${C.border}`, borderRadius: 8, color: C.text, fontSize: 13, padding: "9px 12px", outline: "none", boxSizing: "border-box", fontFamily: "monospace" }}/>
              </div>

              {/* Notification toggles */}
              <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 1, marginBottom: 4 }}>Notify me when…</div>
              <Toggle checked={!!cfg.on_login}    onChange={v => update("on_login", v)}    label="Login attempt"   desc="Any successful or failed login" C={C}/>
              <Toggle checked={!!cfg.on_register} onChange={v => update("on_register", v)} label="New registration" desc="Someone creates a new account"    C={C}/>
              <Toggle checked={!!cfg.on_guest}    onChange={v => update("on_guest", v)}    label="Guest joins"     desc="A guest connects to any room"      C={C}/>
              <Toggle checked={!!cfg.on_temp_room} onChange={v => update("on_temp_room", v)} label="Temp room created" desc="A temp chat room is opened"    C={C}/>

              {/* Test button */}
              {cfg.app_token && cfg.user_key && (
                <div style={{ marginTop: 18 }}>
                  <button onClick={test} disabled={testing}
                    style={{ background: `${C.accent}22`, border: `1px solid ${C.accent}44`, borderRadius: 9, color: C.accent, fontSize: 13, fontWeight: 700, padding: "9px 18px", cursor: "pointer", opacity: testing ? 0.6 : 1 }}>
                    {testing ? "Sending…" : "Send Test Notification"}
                  </button>
                  {testResult === "success" && <span style={{ marginLeft: 12, color: "#22C55E", fontSize: 13, fontWeight: 700 }}>✓ Sent!</span>}
                  {testResult === "error" && <span style={{ marginLeft: 12, color: "#EF4444", fontSize: 13 }}>Failed — check your tokens</span>}
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: "12px 20px", borderTop: `1px solid ${C.border}`, display: "flex", gap: 8, justifyContent: "flex-end", flexShrink: 0 }}>
          <button onClick={onClose} style={{ background: "transparent", border: `1px solid ${C.border}`, borderRadius: 9, color: C.muted, fontSize: 13, padding: "8px 16px", cursor: "pointer" }}>Close</button>
          <button onClick={save} disabled={saving || !dirty}
            style={{ background: dirty ? C.accent : `${C.accent}44`, border: "none", borderRadius: 9, color: dirty ? "#0A0C10" : C.muted, fontWeight: 700, fontSize: 13, padding: "8px 20px", cursor: dirty ? "pointer" : "default", transition: "all 0.15s" }}>
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}
